import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { NumberInput } from '@/components/ui/number-input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatCurrency } from '@/lib/utils';
import { Plus, Truck, X } from 'lucide-react';

export const LANDED_COST_FIELD_KEYS = [
  'insuranceCost',
  'exwCost',
  'freightCost',
  'demurrageCost',
  'withholdingTax',
  'customsDuty',
  'customsOvertimeCost',
  'stampDutyCost',
  'customsPorterageCost',
  'commissionCost',
  'domesticFreightCost',
  'forkliftCost',
  'bankCharges',
] as const;

export type LandedCostKey = (typeof LANDED_COST_FIELD_KEYS)[number];

export type DistributionMethod = 'value' | 'quantity' | 'volume';

export const CURRENCY_OPTIONS = ['EUR', 'USD', 'TRY', 'GBP'] as const;

export const COST_DEFAULTS: Record<
  LandedCostKey,
  { method: DistributionMethod; kdvRate: number; kdvOnly?: boolean }
> = {
  insuranceCost: { method: 'value', kdvRate: 16 },
  exwCost: { method: 'volume', kdvRate: 16 },
  freightCost: { method: 'volume', kdvRate: 16 },
  demurrageCost: { method: 'volume', kdvRate: 0 },
  withholdingTax: { method: 'value', kdvRate: 0, kdvOnly: true },
  customsDuty: { method: 'value', kdvRate: 16, kdvOnly: true },
  customsOvertimeCost: { method: 'value', kdvRate: 16 },
  stampDutyCost: { method: 'value', kdvRate: 0 },
  customsPorterageCost: { method: 'volume', kdvRate: 16 },
  commissionCost: { method: 'value', kdvRate: 16 },
  domesticFreightCost: { method: 'volume', kdvRate: 16 },
  forkliftCost: { method: 'volume', kdvRate: 0 },
  bankCharges: { method: 'value', kdvRate: 0 },
};

export interface LandedCostRow {
  key: LandedCostKey;
  amount: number;
  currency: string;
  kdvRate: number;
  isKdvOnly: boolean;
  amountWithKdv: number;
  method: DistributionMethod;
  manualAllocations: Record<string, number>; // poId -> pct
  sellerId: string | null;
}

export interface LandedCostPOColumn {
  id: string;
  orderNumber: string;
  supplierName: string;
  invoiceNumber: string | null;
  currency: string;
}

/** Row used in the "+KDV" column even when every cost has kdvRate==0. */
export function getEffectiveAmount(c: LandedCostRow): number {
  if (c.isKdvOnly) return 0;
  return c.amount;
}

export function newLandedCostRow(key: LandedCostKey): LandedCostRow {
  const d = COST_DEFAULTS[key];
  return {
    key,
    amount: 0,
    currency: 'TRY',
    kdvRate: d.kdvRate,
    isKdvOnly: !!d.kdvOnly,
    amountWithKdv: 0,
    method: d.method,
    manualAllocations: {},
    sellerId: null,
  };
}

/**
 * Compute the allocation percentage for a cost row against a specific PO,
 * based on the row's distribution method.
 */
export function getAllocationPct(
  cost: LandedCostRow,
  poId: string,
  poSubtotals: Record<string, number>,
  poQuantities: Record<string, number>
): number {
  if (cost.method === 'value') {
    const total = Object.values(poSubtotals).reduce((s, v) => s + v, 0);
    return total > 0 ? ((poSubtotals[poId] ?? 0) / total) * 100 : 0;
  }
  if (cost.method === 'quantity') {
    const total = Object.values(poQuantities).reduce((s, v) => s + v, 0);
    return total > 0 ? ((poQuantities[poId] ?? 0) / total) * 100 : 0;
  }
  return cost.manualAllocations[poId] ?? 0;
}

/** Sum a 'volume' (manual) cost row's allocations across the given POs. */
export function volumeAllocationSum(cost: LandedCostRow, poIds: string[]): number {
  return poIds.reduce((s, poId) => s + (cost.manualAllocations[poId] ?? 0), 0);
}

/**
 * Find 'volume'-method cost rows whose manual per-PO percentages don't sum to
 * ~100%. Value/quantity methods are auto-normalized and can't drift; only
 * manual volume entry can silently under/over-allocate the entered cost
 * (40%+40% loses 20%, 60%+60% inflates by 20%), which then distorts inventory
 * cost and every FIFO-derived price. Callers block submit on a non-empty list.
 */
export function getVolumeAllocationIssues(
  landedCosts: LandedCostRow[],
  poIds: string[]
): Array<{ key: LandedCostKey; sum: number }> {
  const issues: Array<{ key: LandedCostKey; sum: number }> = [];
  for (const c of landedCosts) {
    if (c.method !== 'volume') continue;
    if (getEffectiveAmount(c) <= 0) continue;
    const sum = volumeAllocationSum(c, poIds);
    if (Math.abs(sum - 100) > 0.5) issues.push({ key: c.key, sum });
  }
  return issues;
}

interface LandedCostsTableProps {
  landedCosts: LandedCostRow[];
  setLandedCosts: Dispatch<SetStateAction<LandedCostRow[]>>;
  pos: LandedCostPOColumn[];
  poSubtotals: Record<string, number>;
  poQuantities: Record<string, number>;
  sellers: Array<{ id: string; companyName: string }>;
}

export function LandedCostsTable({
  landedCosts,
  setLandedCosts,
  pos,
  poSubtotals,
  poQuantities,
  sellers,
}: LandedCostsTableProps) {
  const { t } = useTranslation('trading');

  const poIds = pos.map((p) => p.id);
  const posById = Object.fromEntries(pos.map((p) => [p.id, p]));

  const updateCostAmount = (index: number, amount: number) => {
    setLandedCosts((prev) =>
      prev.map((c, i) => {
        if (i !== index) return c;
        const amountWithKdv =
          c.kdvRate > 0 ? Math.round(amount * (1 + c.kdvRate / 100) * 100) / 100 : 0;
        return { ...c, amount, amountWithKdv };
      })
    );
  };

  const updateCostAmountWithKdv = (index: number, amountWithKdv: number) => {
    setLandedCosts((prev) =>
      prev.map((c, i) => {
        if (i !== index) return c;
        if (c.isKdvOnly) return { ...c, amountWithKdv };
        const amount =
          c.kdvRate > 0 ? Math.round((amountWithKdv / (1 + c.kdvRate / 100)) * 100) / 100 : 0;
        return { ...c, amount, amountWithKdv };
      })
    );
  };

  const updateCostKdvRate = (index: number, kdvRate: number) => {
    setLandedCosts((prev) =>
      prev.map((c, i) => {
        if (i !== index) return c;
        const amountWithKdv =
          kdvRate > 0 && c.amount > 0 ? Math.round(c.amount * (1 + kdvRate / 100) * 100) / 100 : 0;
        return { ...c, kdvRate, amountWithKdv };
      })
    );
  };

  const updateCostCurrency = (index: number, currency: string) => {
    setLandedCosts((prev) => prev.map((c, i) => (i === index ? { ...c, currency } : c)));
  };

  const updateCostMethod = (index: number, method: DistributionMethod) => {
    setLandedCosts((prev) => prev.map((c, i) => (i === index ? { ...c, method } : c)));
  };

  const updateCostSeller = (index: number, sellerId: string | null) => {
    setLandedCosts((prev) => prev.map((c, i) => (i === index ? { ...c, sellerId } : c)));
  };

  const updateManualAllocation = (index: number, poId: string, value: number) => {
    setLandedCosts((prev) =>
      prev.map((c, i) =>
        i === index ? { ...c, manualAllocations: { ...c.manualAllocations, [poId]: value } } : c
      )
    );
  };

  const removeCostRow = (index: number) => {
    setLandedCosts((prev) => prev.filter((_, i) => i !== index));
  };

  const addCostRow = (key: LandedCostKey) => {
    setLandedCosts((prev) => [...prev, newLandedCostRow(key)]);
  };

  const applyToAllManual = () => {
    const firstManual = landedCosts.find((c) => c.method === 'volume' && c.amount > 0);
    if (!firstManual) return;
    setLandedCosts((prev) =>
      prev.map((c) =>
        c.method === 'volume'
          ? { ...c, manualAllocations: { ...firstManual.manualAllocations } }
          : c
      )
    );
  };

  const availableCostKeys = LANDED_COST_FIELD_KEYS.filter(
    (key) => !landedCosts.some((c) => c.key === key)
  );

  // Footer totals — base amount column + +KDV column, each by currency
  const amountByCurrency: Record<string, number> = {};
  const kdvTotalByCurrency: Record<string, number> = {};
  for (const c of landedCosts) {
    const amt = getEffectiveAmount(c);
    if (amt > 0) {
      amountByCurrency[c.currency] = (amountByCurrency[c.currency] ?? 0) + amt;
    }
    if (c.isKdvOnly && c.amountWithKdv > 0) {
      kdvTotalByCurrency[c.currency] = (kdvTotalByCurrency[c.currency] ?? 0) + c.amountWithKdv;
    } else if (c.kdvRate > 0 && c.amountWithKdv > 0) {
      kdvTotalByCurrency[c.currency] = (kdvTotalByCurrency[c.currency] ?? 0) + c.amountWithKdv;
    } else if (c.amount > 0) {
      kdvTotalByCurrency[c.currency] = (kdvTotalByCurrency[c.currency] ?? 0) + c.amount;
    }
  }

  const formatMultiCurrency = (totals: Record<string, number>) =>
    Object.entries(totals)
      .filter(([, amt]) => amt > 0)
      .map(([cur, amt]) => formatCurrency(amt, cur))
      .join(' + ');

  const anyHasKdv = landedCosts.some((c) => c.kdvRate > 0 || c.isKdvOnly);

  const colWidths = {
    costType: 180,
    amount: 90,
    currency: 52,
    kdvPct: 56,
    kdvAmount: anyHasKdv ? 90 : 0,
    method: 72,
    accountCard: 150,
    remove: 28,
  };
  const stickyOffsets = {
    costType: 0,
    amount: colWidths.costType,
    currency: colWidths.costType + colWidths.amount,
    kdvPct: colWidths.costType + colWidths.amount + colWidths.currency,
    kdvAmount: colWidths.costType + colWidths.amount + colWidths.currency + colWidths.kdvPct,
    method:
      colWidths.costType +
      colWidths.amount +
      colWidths.currency +
      colWidths.kdvPct +
      colWidths.kdvAmount,
    accountCard:
      colWidths.costType +
      colWidths.amount +
      colWidths.currency +
      colWidths.kdvPct +
      colWidths.kdvAmount +
      colWidths.method,
    remove:
      colWidths.costType +
      colWidths.amount +
      colWidths.currency +
      colWidths.kdvPct +
      colWidths.kdvAmount +
      colWidths.method +
      colWidths.accountCard,
  };
  const totalStickyWidth = stickyOffsets.remove + colWidths.remove;
  const stickyBase = 'sticky bg-inherit z-10';
  const stickyHeaderBase = 'sticky z-20';

  return (
    <div className="border-border bg-card overflow-hidden border">
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <Label className="flex items-center gap-2 text-sm font-semibold">
          <Truck className="h-4 w-4" />
          {t('purchasing.goodsReceipts.landedCosts')}
        </Label>
        <div className="flex gap-2">
          {landedCosts.some((c) => c.method === 'volume') && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={applyToAllManual}
            >
              {t('purchasing.goodsReceipts.applyToAll')}
            </Button>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={availableCostKeys.length === 0}
              >
                <Plus className="mr-1 h-3 w-3" />
                {t('purchasing.goodsReceipts.addCost')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="end">
              {availableCostKeys.length === 0 ? (
                <p className="text-muted-foreground p-3 text-sm">
                  {t('purchasing.goodsReceipts.allCostTypesAdded')}
                </p>
              ) : (
                <div className="max-h-60 overflow-y-auto">
                  {availableCostKeys.map((key) => (
                    <button
                      key={key}
                      type="button"
                      className="hover:bg-muted/50 w-full border-b px-3 py-2 text-left text-sm last:border-b-0"
                      onClick={() => addCostRow(key)}
                    >
                      {t(`purchasing.goodsReceipts.lc_${key}`)}
                    </button>
                  ))}
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table
          className="w-full border-collapse text-sm"
          style={{ minWidth: totalStickyWidth + poIds.length * 110 }}
        >
          <thead className="bg-muted/50">
            <tr>
              <th
                className={`${stickyHeaderBase} bg-muted/50 whitespace-nowrap px-2 py-1.5 text-left text-xs`}
                style={{
                  left: stickyOffsets.costType,
                  width: colWidths.costType,
                  minWidth: colWidths.costType,
                }}
              >
                {t('purchasing.goodsReceipts.costType')}
              </th>
              <th
                className={`${stickyHeaderBase} bg-muted/50 px-2 py-1.5 text-right text-xs`}
                style={{
                  left: stickyOffsets.amount,
                  width: colWidths.amount,
                  minWidth: colWidths.amount,
                }}
              >
                {t('purchasing.goodsReceipts.amount')}
              </th>
              <th
                className={`${stickyHeaderBase} bg-muted/50 px-1 py-1.5 text-center text-xs`}
                style={{
                  left: stickyOffsets.currency,
                  width: colWidths.currency,
                  minWidth: colWidths.currency,
                }}
              ></th>
              <th
                className={`${stickyHeaderBase} bg-muted/50 px-1 py-1.5 text-center text-xs`}
                style={{
                  left: stickyOffsets.kdvPct,
                  width: colWidths.kdvPct,
                  minWidth: colWidths.kdvPct,
                }}
                title="KDV %"
              >
                KDV%
              </th>
              {anyHasKdv && (
                <th
                  className={`${stickyHeaderBase} bg-muted/50 px-2 py-1.5 text-right text-xs`}
                  style={{
                    left: stickyOffsets.kdvAmount,
                    width: colWidths.kdvAmount,
                    minWidth: colWidths.kdvAmount,
                  }}
                >
                  +KDV
                </th>
              )}
              <th
                className={`${stickyHeaderBase} bg-muted/50 px-2 py-1.5 text-center text-xs`}
                style={{
                  left: stickyOffsets.method,
                  width: colWidths.method,
                  minWidth: colWidths.method,
                }}
              >
                {t('purchasing.goodsReceipts.distributionMethod')}
              </th>
              <th
                className={`${stickyHeaderBase} bg-muted/50 whitespace-nowrap px-2 py-1.5 text-left text-xs`}
                style={{
                  left: stickyOffsets.accountCard,
                  width: colWidths.accountCard,
                  minWidth: colWidths.accountCard,
                }}
              >
                {t('purchasing.goodsReceipts.accountCard')}
              </th>
              <th
                className={`${stickyHeaderBase} bg-muted/50 border-border border-r px-1 py-1.5`}
                style={{
                  left: stickyOffsets.remove,
                  width: colWidths.remove,
                  minWidth: colWidths.remove,
                }}
              ></th>
              {poIds.map((poId) => {
                const col = posById[poId];
                return (
                  <th
                    key={poId}
                    className="border-border min-w-[110px] border-l px-2 py-1.5 text-center"
                  >
                    <div className="font-mono text-xs">{col?.orderNumber}</div>
                    {col?.invoiceNumber && (
                      <div className="text-primary text-[10px] font-medium font-normal">
                        {col.invoiceNumber}
                      </div>
                    )}
                    <div className="text-muted-foreground truncate text-[10px] font-normal">
                      {col?.supplierName}
                    </div>
                    <div className="text-muted-foreground text-[10px] font-normal">
                      {col?.currency}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y">
            {landedCosts.map((cost, costIndex) => {
              const rowBg = cost.method === 'volume' ? 'bg-amber-50/50' : 'bg-card';
              return (
                <tr key={`${cost.key}-${costIndex}`} className={rowBg}>
                  <td
                    className={`${stickyBase} whitespace-nowrap px-2 py-1 text-xs font-medium ${rowBg}`}
                    style={{
                      left: stickyOffsets.costType,
                      width: colWidths.costType,
                      minWidth: colWidths.costType,
                    }}
                  >
                    {t(`purchasing.goodsReceipts.lc_${cost.key}`)}
                    {cost.method === 'volume' &&
                      getEffectiveAmount(cost) > 0 &&
                      (() => {
                        const sum = volumeAllocationSum(cost, poIds);
                        const ok = Math.abs(sum - 100) <= 0.5;
                        return (
                          <div
                            className={`text-[10px] font-normal ${ok ? 'text-muted-foreground' : 'text-destructive'}`}
                            title={t('purchasing.goodsReceipts.volumeSumHint')}
                          >
                            Σ {sum.toFixed(0)}%
                          </div>
                        );
                      })()}
                  </td>
                  <td
                    className={`${stickyBase} px-2 py-1 ${rowBg}`}
                    style={{
                      left: stickyOffsets.amount,
                      width: colWidths.amount,
                      minWidth: colWidths.amount,
                    }}
                  >
                    {cost.isKdvOnly ? (
                      <span className="text-muted-foreground flex justify-end text-xs">—</span>
                    ) : (
                      <NumberInput
                        value={cost.amount || ''}
                        onValueChange={(v) => updateCostAmount(costIndex, v ?? 0)}
                        decimalScale={2}
                        placeholder="0.00"
                        className="h-6 w-[75px] text-right text-xs"
                      />
                    )}
                  </td>
                  <td
                    className={`${stickyBase} px-0.5 py-1 text-center ${rowBg}`}
                    style={{
                      left: stickyOffsets.currency,
                      width: colWidths.currency,
                      minWidth: colWidths.currency,
                    }}
                  >
                    <select
                      value={cost.currency}
                      onChange={(e) => updateCostCurrency(costIndex, e.target.value)}
                      className="border-border bg-background w-[48px] rounded-sm border px-0 py-0.5 text-center text-[10px]"
                    >
                      {CURRENCY_OPTIONS.map((cur) => (
                        <option key={cur} value={cur}>
                          {cur}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td
                    className={`${stickyBase} px-1 py-1 text-center ${rowBg}`}
                    style={{
                      left: stickyOffsets.kdvPct,
                      width: colWidths.kdvPct,
                      minWidth: colWidths.kdvPct,
                    }}
                  >
                    {cost.isKdvOnly ? (
                      <span className="text-muted-foreground text-xs">—</span>
                    ) : (
                      <NumberInput
                        value={cost.kdvRate}
                        onValueChange={(v) => updateCostKdvRate(costIndex, v ?? 0)}
                        decimalScale={0}
                        isAllowed={({ floatValue }) =>
                          floatValue === undefined || (floatValue >= 0 && floatValue <= 100)
                        }
                        placeholder="0"
                        suffix="%"
                        className="h-6 w-[48px] text-right text-xs"
                      />
                    )}
                  </td>
                  {anyHasKdv && (
                    <td
                      className={`${stickyBase} px-2 py-1 ${rowBg}`}
                      style={{
                        left: stickyOffsets.kdvAmount,
                        width: colWidths.kdvAmount,
                        minWidth: colWidths.kdvAmount,
                      }}
                    >
                      {cost.isKdvOnly ? (
                        <NumberInput
                          value={cost.amountWithKdv || ''}
                          onValueChange={(v) => updateCostAmountWithKdv(costIndex, v ?? 0)}
                          decimalScale={2}
                          placeholder="0.00"
                          className="h-6 w-[75px] text-right text-xs"
                        />
                      ) : cost.kdvRate > 0 ? (
                        <NumberInput
                          value={cost.amountWithKdv || ''}
                          onValueChange={(v) => updateCostAmountWithKdv(costIndex, v ?? 0)}
                          decimalScale={2}
                          placeholder="0.00"
                          className="h-6 w-[75px] text-right text-xs"
                        />
                      ) : (
                        <span className="text-muted-foreground flex justify-end text-xs">—</span>
                      )}
                    </td>
                  )}
                  <td
                    className={`${stickyBase} px-1 py-1 text-center ${rowBg}`}
                    style={{
                      left: stickyOffsets.method,
                      width: colWidths.method,
                      minWidth: colWidths.method,
                    }}
                  >
                    <select
                      value={cost.method}
                      onChange={(e) =>
                        updateCostMethod(costIndex, e.target.value as DistributionMethod)
                      }
                      className="border-border bg-background w-[60px] rounded-sm border px-0.5 py-0.5 text-xs"
                    >
                      <option value="value">{t('purchasing.goodsReceipts.distByValue')}</option>
                      <option value="quantity">
                        {t('purchasing.goodsReceipts.distByQuantity')}
                      </option>
                      <option value="volume">{t('purchasing.goodsReceipts.distByVolume')}</option>
                    </select>
                  </td>
                  <td
                    className={`${stickyBase} px-1 py-1 ${rowBg}`}
                    style={{
                      left: stickyOffsets.accountCard,
                      width: colWidths.accountCard,
                      minWidth: colWidths.accountCard,
                    }}
                  >
                    <select
                      value={cost.sellerId ?? ''}
                      onChange={(e) => updateCostSeller(costIndex, e.target.value || null)}
                      className="border-border bg-background w-[140px] truncate rounded-sm border px-1 py-0.5 text-xs"
                    >
                      <option value="">—</option>
                      {sellers.map((seller) => (
                        <option key={seller.id} value={seller.id}>
                          {seller.companyName}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td
                    className={`${stickyBase} border-border border-r px-1 py-1 text-center ${rowBg}`}
                    style={{
                      left: stickyOffsets.remove,
                      width: colWidths.remove,
                      minWidth: colWidths.remove,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => removeCostRow(costIndex)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </td>
                  {poIds.map((poId) => {
                    const pct = getAllocationPct(cost, poId, poSubtotals, poQuantities);
                    const effectiveAmt = getEffectiveAmount(cost);
                    const allocated = (effectiveAmt * pct) / 100;
                    return (
                      <td key={poId} className="border-border border-l px-2 py-1 text-center">
                        {cost.method === 'volume' ? (
                          <div>
                            <div className="flex items-center justify-center gap-0.5">
                              <NumberInput
                                value={cost.manualAllocations[poId] || ''}
                                onValueChange={(v) =>
                                  updateManualAllocation(costIndex, poId, v ?? 0)
                                }
                                decimalScale={2}
                                isAllowed={({ floatValue }) =>
                                  floatValue === undefined || (floatValue >= 0 && floatValue <= 100)
                                }
                                placeholder="0"
                                className="h-6 w-[60px] text-right text-xs"
                              />
                              <span className="text-muted-foreground text-xs">%</span>
                            </div>
                            {effectiveAmt > 0 && (
                              <div className="mt-0.5 text-[10px] text-amber-700">
                                {formatCurrency(allocated, cost.currency)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            {effectiveAmt > 0 ? (
                              <>
                                <div
                                  className={`text-xs font-medium ${
                                    cost.method === 'value' ? 'text-blue-700' : 'text-green-700'
                                  }`}
                                >
                                  {formatCurrency(allocated, cost.currency)}
                                </div>
                                <div className="text-muted-foreground text-[10px]">
                                  {pct.toFixed(1)}%
                                </div>
                              </>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-muted/30">
            <tr>
              <td
                className={`${stickyBase} bg-muted/30 px-2 py-1.5 text-xs font-semibold`}
                style={{ left: stickyOffsets.costType }}
              >
                {t('purchasing.goodsReceipts.totalLandedCosts')}
              </td>
              <td
                className={`${stickyBase} bg-muted/30 px-2 py-1.5 text-right text-xs font-bold`}
                style={{ left: stickyOffsets.amount }}
              >
                {formatMultiCurrency(amountByCurrency) || '—'}
              </td>
              <td
                className={`${stickyBase} bg-muted/30 px-1 py-1.5`}
                style={{ left: stickyOffsets.currency }}
              ></td>
              <td
                className={`${stickyBase} bg-muted/30 px-1 py-1.5`}
                style={{ left: stickyOffsets.kdvPct }}
              ></td>
              {anyHasKdv && (
                <td
                  className={`${stickyBase} bg-muted/30 px-2 py-1.5 text-right text-xs font-bold`}
                  style={{ left: stickyOffsets.kdvAmount }}
                >
                  {formatMultiCurrency(kdvTotalByCurrency) || '—'}
                </td>
              )}
              <td
                className={`${stickyBase} bg-muted/30 px-2 py-1.5`}
                style={{ left: stickyOffsets.method }}
              ></td>
              <td
                className={`${stickyBase} bg-muted/30 px-2 py-1.5`}
                style={{ left: stickyOffsets.accountCard }}
              ></td>
              <td
                className={`${stickyBase} bg-muted/30 border-border border-r px-1 py-1.5`}
                style={{ left: stickyOffsets.remove }}
              ></td>
              {poIds.map((poId) => {
                const poTotals: Record<string, number> = {};
                for (const cost of landedCosts) {
                  const effAmt = getEffectiveAmount(cost);
                  if (effAmt <= 0) continue;
                  const allocated =
                    (effAmt * getAllocationPct(cost, poId, poSubtotals, poQuantities)) / 100;
                  poTotals[cost.currency] = (poTotals[cost.currency] ?? 0) + allocated;
                }
                return (
                  <td
                    key={poId}
                    className="border-border border-l px-2 py-1.5 text-center text-xs font-semibold"
                  >
                    {formatMultiCurrency(poTotals) || '—'}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
